import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { sharedMutators } from "../../../../shared/mutators";
import { formatRelativeTime } from "../../../lib/formatRelativeTime";
import { TestConnectionButton } from "../../TestConnectionButton";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";

const Container = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  overflow: hidden;
`;

const Pill = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 6px 8px;
  gap: 4px;
`;

const Indicator = styled("div")`
  position: relative;
  width: 12px;
  height: 12px;

  &::before {
    content: "";
    position: absolute;
    width: 10px;
    height: 10px;
    left: 1px;
    top: 1px;
    border-radius: 10px;
    background: var(--400);
  }

  &::after {
    content: "";
    position: absolute;
    width: 6px;
    height: 6px;
    left: 3px;
    top: 3px;
    border-radius: 10px;
    background: var(--600);
  }
`;

type Destination = {
  id: string;
  lastError: string | null;
  cursorTimestamp: number;
  testingStartedAt: number | null;
};

type Props = {
  destination: Destination;
};

export function DestinationStatusPill({ destination }: Props) {
  const z = useZero();
  const isPending = destination.testingStartedAt !== null;

  const failed = destination.lastError !== null;
  const color = isPending ? "gray" : failed ? "red" : "green";
  const label = isPending ? "Testing" : failed ? "Error" : "OK";

  const tooltip = destination.lastError
    ? destination.lastError
    : `Last sent ${formatRelativeTime(destination.cursorTimestamp)}`;

  return (
    <Tooltip content={tooltip} contentStyle={{ overflowWrap: "anywhere" }}>
      <Container>
        <Pill>
          <Indicator
            style={
              {
                "--400": `var(--${color}-400)`,
                "--600": `var(--${color}-600)`,
              } as React.CSSProperties
            }
          />
          <Text
            variant="text-small-md"
            color={isPending ? "var(--text-secondary)" : "var(--text-primary)"}
          >
            {label}
          </Text>
        </Pill>
        <TestConnectionButton
          running={isPending}
          onTest={() =>
            z.mutate(
              sharedMutators.testAuditLogDestination({ id: destination.id })
            )
          }
        />
      </Container>
    </Tooltip>
  );
}
